// ============================================================================
// THEME CONFIGURATION — TENANT-AWARE PRESET LOADER
// ============================================================================
// The active theme is resolved from VITE_THEME env var:
//   VITE_THEME=wayak → src/config/themes/wayak.json
//   VITE_THEME=dumax → src/config/themes/dumax.json
//
// Fallback chain: {tenant}.json → grayscale.json
//
// Usage:
//   import { brand, colors, fonts, assets } from "./config/theme";
//   import theme from "./config/theme";
// ============================================================================

import type { ThemePreset } from "../theme/preset";

export type { ThemePreset, ThemeColors, ColorScale } from "../theme/preset";
export { scaleOf } from "../theme/preset";

// ---------------------------------------------------------------------------
// Load all theme JSON files eagerly at build time
// ---------------------------------------------------------------------------
const themeModules = import.meta.glob<{ default: ThemePreset }>(
  "./themes/*.json",
  { eager: true },
);

// ---------------------------------------------------------------------------
// Resolve active theme: {VITE_THEME}.json → grayscale.json
// ---------------------------------------------------------------------------
const tenant = import.meta.env.VITE_THEME as string | undefined;

function resolveThemeModule(key: string): ThemePreset | undefined {
  const mod = themeModules[key];
  if (!mod) return undefined;
  return (mod.default ?? mod) as unknown as ThemePreset;
}

const activePreset: ThemePreset =
  (tenant ? resolveThemeModule(`./themes/${tenant}.json`) : undefined) ??
  resolveThemeModule("./themes/grayscale.json")!;

// ---------------------------------------------------------------------------
// 1. BRAND COLORS
// ---------------------------------------------------------------------------
export const brand = activePreset.brand;

// ---------------------------------------------------------------------------
// 2. COLOR PALETTE
// ---------------------------------------------------------------------------
export const colors = activePreset.colors;

// ---------------------------------------------------------------------------
// 3. TYPOGRAPHY (merged with icon URLs that don't change per theme)
// ---------------------------------------------------------------------------
export const fonts = {
  ...activePreset.fonts,
  icons: {
    remixicon: "https://cdn.jsdelivr.net/npm/remixicon@4.3.0/fonts/remixicon.css",
    materialIcons: "https://fonts.googleapis.com/icon?family=Material+Icons",
    materialSymbols:
      "https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined",
  },
};

// ---------------------------------------------------------------------------
// 4. LOGO & BRAND ASSETS (filenames — actual imports in brandAssets.ts)
// ---------------------------------------------------------------------------
export const assets = activePreset.assets;

// ---------------------------------------------------------------------------
// 5. SHADOWS & EFFECTS
// ---------------------------------------------------------------------------
export const shadows = activePreset.shadows;

// ---------------------------------------------------------------------------
// 6. BORDER RADIUS
// ---------------------------------------------------------------------------
export const radii = activePreset.radii;

// ---------------------------------------------------------------------------
// 7. Z-INDEX SCALE
// ---------------------------------------------------------------------------
export const zIndex = activePreset.zIndex;

// ---------------------------------------------------------------------------
// 8. PWA / META
// ---------------------------------------------------------------------------
export const pwa = {
  ...activePreset.pwa,
  themeColor: brand.accent,
  backgroundColor: colors.light.bg,
};

// ---------------------------------------------------------------------------
// 9. GRADIENT PRESETS (computed from active brand)
// ---------------------------------------------------------------------------
export const gradients = {
  logoBg: `linear-gradient(to bottom right, ${brand.primary}, ${brand.primaryHover})`,
  // Use static Tailwind classes (from-primary-500 to-primary-400) in components instead.
  // Dynamic class interpolation with an interpolated hex breaks Tailwind JIT.
  logoBgClass: "from-primary-500 to-primary-400",
};

// ---------------------------------------------------------------------------
// 10. ACTIVE THEME ID (useful for conditional logic in components)
// ---------------------------------------------------------------------------
export const activeThemeId = activePreset.id;

// ---------------------------------------------------------------------------
// 11. AGGREGATED THEME OBJECT
// ---------------------------------------------------------------------------
export const theme = {
  id: activePreset.id,
  name: activePreset.name,
  brand,
  colors,
  fonts,
  assets,
  shadows,
  radii,
  zIndex,
  pwa,
  gradients,
};

export type Theme = typeof theme;

export default theme;
